/**
 * Ad Manager Service
 *
 * Wraps the Publift Fuse tag API. Fuse loads its own GPT/Prebid stack and
 * runs auctions across 50+ demand partners. Our responsibilities:
 *   1. Register zones when AdSlot components mount (after their <div> is in the DOM)
 *   2. Call pageInit() on every SPA route change
 *
 * Initial pageInit is fired eagerly from index.html's head script — see
 * Publift's recommended pattern in the dynamic-site-initialisation docs.
 *
 * Fuse API: https://docs.publift.com/fuse-web/technical-reference/api-reference/
 */

import { AD_UNITS, REWARDED_AD_UNIT_PATH } from 'src/config/ads';

const STICKY_FOOTER_AD_UNIT_PATH = '/21870650930/pupvote-sticky-footer';

let stickyFooterSuppressionActive = false;
let stickyFooterSuppressionListeners = null;
const destroyedStickyFooterSlots = new WeakSet();

/**
 * Guard shared by every sticky-footer suppression call site: only touch the
 * slot when the ad stack is actually present (prod with Fuse/GPT loaded).
 * Checked before fuse() because fuse() fabricates window.fusetag on dev.
 */
export function canControlStickyFooterAd() {
  return typeof window !== 'undefined' && Boolean(window.googletag || window.fusetag);
}

function fuse() {
  if (typeof window === 'undefined') return null;
  window.fusetag = window.fusetag || { que: [] };
  return window.fusetag;
}

function googletagWithCommandQueue() {
  if (typeof window === 'undefined' || !window.googletag) return null;
  if (!window.googletag.cmd || typeof window.googletag.cmd.push !== 'function') return null;
  return window.googletag;
}

function normalizeAdUnitPath(path) {
  return String(path || '').replace(/^\/+/, '');
}

function isStickyFooterSlot(slot) {
  if (!slot || typeof slot.getAdUnitPath !== 'function') return false;
  return normalizeAdUnitPath(slot.getAdUnitPath()) === normalizeAdUnitPath(STICKY_FOOTER_AD_UNIT_PATH);
}

function destroyStickyFooterSlotsNow(googletag, slots) {
  if (!googletag || typeof googletag.destroySlots !== 'function') return;

  const stickySlots = (slots || [])
    .filter(isStickyFooterSlot)
    .filter((slot) => !destroyedStickyFooterSlots.has(slot));

  if (stickySlots.length > 0) {
    const destroyed = googletag.destroySlots(stickySlots);
    if (destroyed) {
      stickySlots.forEach((slot) => destroyedStickyFooterSlots.add(slot));
    }
  }
}

function destroyRegisteredStickyFooterSlots(googletag) {
  if (!googletag || typeof googletag.pubads !== 'function') return;
  const pubads = googletag.pubads();
  if (!pubads || typeof pubads.getSlots !== 'function') return;
  destroyStickyFooterSlotsNow(googletag, pubads.getSlots());
}

function addPubadsListener(pubads, eventName, listener) {
  if (!pubads || typeof pubads.addEventListener !== 'function') return;
  pubads.addEventListener(eventName, listener);
}

function removePubadsListener(pubads, eventName, listener) {
  if (!pubads || typeof pubads.removeEventListener !== 'function') return;
  pubads.removeEventListener(eventName, listener);
}

function installStickyFooterSuppression(googletag) {
  if (!googletag || typeof googletag.pubads !== 'function' || stickyFooterSuppressionListeners) return;

  const pubads = googletag.pubads();
  const destroyEventSlot = (ev) => {
    if (!stickyFooterSuppressionActive || !ev || !isStickyFooterSlot(ev.slot)) return;
    destroyStickyFooterSlotsNow(googletag, [ev.slot]);
  };

  stickyFooterSuppressionListeners = { destroyEventSlot };
  addPubadsListener(pubads, 'slotRequested', destroyEventSlot);
  addPubadsListener(pubads, 'slotRenderEnded', destroyEventSlot);
  addPubadsListener(pubads, 'slotOnload', destroyEventSlot);
}

function removeStickyFooterSuppression(googletag) {
  if (!googletag || typeof googletag.pubads !== 'function' || !stickyFooterSuppressionListeners) return;

  const pubads = googletag.pubads();
  const { destroyEventSlot } = stickyFooterSuppressionListeners;
  removePubadsListener(pubads, 'slotRequested', destroyEventSlot);
  removePubadsListener(pubads, 'slotRenderEnded', destroyEventSlot);
  removePubadsListener(pubads, 'slotOnload', destroyEventSlot);
  stickyFooterSuppressionListeners = null;
}

function runWhenFuseAndGoogletagAreReady(callback) {
  const googletag = googletagWithCommandQueue();
  if (googletag) {
    googletag.cmd.push(() => callback(googletag));
    return true;
  }

  const ft = fuse();
  if (!ft || !ft.que || typeof ft.que.push !== 'function') return false;
  ft.que.push(() => {
    const queuedGoogletag = googletagWithCommandQueue();
    if (queuedGoogletag) {
      queuedGoogletag.cmd.push(() => callback(queuedGoogletag));
    }
  });
  return true;
}

/**
 * Idempotent. Initial pageInit is fired in the head; this is kept for
 * backward compatibility with App.js bootstrap.
 */
export function init() {}

/**
 * Notify Fuse that a new page has loaded (SPA route change).
 * Refreshes persistent zones (sticky footer) and runs auctions for any
 * newly-mounted zones on the new route.
 */
export function triggerPageInit() {
  const ft = fuse();
  if (!ft) return;
  ft.que.push(() => {
    if (typeof ft.pageInit === 'function') {
      ft.pageInit();
    }
  });
}

/**
 * Remove Fuse's persistent sticky footer/anchor GPT slot during onboarding.
 *
 * Fuse does not expose a sticky-footer zone id anywhere in this codebase, so we
 * target the actual GPT slot path instead. The event listeners keep suppression
 * active for mobile anchor slots that Fuse defines after the first command runs.
 */
export function destroyStickyFooterSlot() {
  stickyFooterSuppressionActive = true;
  return runWhenFuseAndGoogletagAreReady((googletag) => {
    if (!stickyFooterSuppressionActive) return;
    installStickyFooterSuppression(googletag);
    destroyRegisteredStickyFooterSlots(googletag);
  });
}

/**
 * Allow Fuse to recreate the persistent sticky footer after onboarding exits.
 *
 * There is no repo-visible sticky-footer data-fuse element or destroyZone-safe
 * zone id to re-register directly. The supported SPA path available here is
 * Fuse pageInit(), which re-initializes persistent zones when leaving /start.
 */
export function reInitStickyFooterSlot() {
  stickyFooterSuppressionActive = false;
  const queued = runWhenFuseAndGoogletagAreReady((googletag) => {
    removeStickyFooterSuppression(googletag);
  });
  triggerPageInit();
  return queued;
}

/**
 * Register an ad zone with Fuse. Called by each <AdSlot> on mount, after
 * the <div data-fuse="..."> is in the DOM.
 *
 * Note: we do NOT call destroyZone on unmount. Per Publift's API reference,
 * destroyZone is "a destructive action that cannot be undone... should only
 * be used for permanent removal, not to pause or deactivate slots." Each
 * AdSlot mount uses a unique element ID, so DOM removal alone is sufficient.
 *
 * @param {string} unitName - key from AD_UNITS config
 * @param {string} elementId - the DOM id of the zone div
 */
export function registerSlot(unitName, elementId) {
  const unit = AD_UNITS[unitName];
  if (!unit) return;

  const ft = fuse();
  if (!ft) return;

  ft.que.push(() => {
    if (typeof ft.registerZone === 'function') {
      ft.registerZone(elementId);
    }
  });
}

/**
 * Check if Fuse is loaded and ready.
 */
export function isReady() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.fusetag);
}

/**
 * Register a Google rewarded-video ad slot directly via GPT.
 *
 * Fuse handles display ad zones via the data-fuse attribute, but rewarded
 * ads use a different GPT API (defineOutOfPageSlot with REWARDED format)
 * and a separate demand pool (AdSense/AdX backfill in our own GAM network).
 * Fuse loads GPT internally so window.googletag is available.
 *
 * Returns a controller `{ destroy }` or null if rewarded inventory is
 * unavailable on this page (GPT returns null for defineOutOfPageSlot in
 * that case — happens on viewports without proper meta-viewport, or when
 * the ad unit isn't configured for rewarded format).
 *
 * Callbacks are wired through GPT's pubads event listeners. We tag listeners
 * with the slot reference so we can remove them on destroy and avoid leaks
 * across multiple ad watches in the same session.
 *
 * @param {object} opts
 * @param {string} opts.customData - the "{user_id}|{nonce}" string for SSV binding
 * @param {object} [opts.targeting] - additional GPT key-values
 * @param {function} [opts.onLoaded]     - rewardedSlotReady fires; ad will auto-show
 * @param {function} [opts.onCompleted]  - rewardedSlotGranted (server reward unlocked)
 * @param {function} [opts.onClosed]     - rewardedSlotClosed; arg: {rewardGranted}
 * @param {function} [opts.onNoFill]     - slotRenderEnded with isEmpty=true
 *
 * @see https://developers.google.com/publisher-tag/samples/display-rewarded-ad
 */
export function registerRewardedSlot({
  customData, targeting,
  onLoaded, onCompleted, onClosed, onNoFill,
} = {}) {
  if (typeof window === 'undefined' || !window.googletag) {
    onNoFill?.();
    return null;
  }
  const { googletag } = window;

  let slot = null;
  let rewardGranted = false;
  let listeners = null; // populated inside cmd.push so we can remove them later

  googletag.cmd.push(() => {
    slot = googletag.defineOutOfPageSlot(
      REWARDED_AD_UNIT_PATH,
      googletag.enums.OutOfPageFormat.REWARDED,
    );
    if (!slot) {
      onNoFill?.();
      return;
    }

    if (targeting) {
      Object.entries(targeting).forEach(([k, v]) => {
        if (v != null && v !== '') slot.setTargeting(k, String(v));
      });
    }
    if (customData) {
      // GPT forwards this to the SSV callback's `custom_data` query param.
      slot.setTargeting('cust_params_nonce', customData);
    }

    slot.addService(googletag.pubads());

    const onReady = (ev) => {
      if (ev.slot !== slot) return;
      onLoaded?.();
      // Spec: makeRewardedVisibleFn lives on the event payload; calling it
      // makes the rewarded ad visible to the user. Some GPT builds expose it
      // directly on the event, others auto-show — check both shapes.
      if (typeof ev.makeRewardedVisible === 'function') {
        ev.makeRewardedVisible();
      } else if (typeof ev.makeRewardedVisibleFn === 'function') {
        ev.makeRewardedVisibleFn();
      }
    };
    const onGranted = (ev) => {
      if (ev.slot !== slot) return;
      rewardGranted = true;
      onCompleted?.({ payload: ev.payload });
    };
    const onSlotClosed = (ev) => {
      if (ev.slot !== slot) return;
      onClosed?.({ rewardGranted });
    };
    const onRenderEnded = (ev) => {
      if (ev.slot !== slot) return;
      if (ev.isEmpty) onNoFill?.();
    };

    googletag.pubads().addEventListener('rewardedSlotReady', onReady);
    googletag.pubads().addEventListener('rewardedSlotGranted', onGranted);
    googletag.pubads().addEventListener('rewardedSlotClosed', onSlotClosed);
    googletag.pubads().addEventListener('slotRenderEnded', onRenderEnded);

    listeners = {
      onReady, onGranted, onSlotClosed, onRenderEnded,
    };

    googletag.display(slot);
    googletag.pubads().refresh([slot]);
  });

  return {
    destroy() {
      googletag.cmd.push(() => {
        if (listeners) {
          const pa = googletag.pubads();
          pa.removeEventListener('rewardedSlotReady', listeners.onReady);
          pa.removeEventListener('rewardedSlotGranted', listeners.onGranted);
          pa.removeEventListener('rewardedSlotClosed', listeners.onSlotClosed);
          pa.removeEventListener('slotRenderEnded', listeners.onRenderEnded);
        }
        if (slot) googletag.destroySlots([slot]);
      });
    },
  };
}

const adManager = {
  init,
  canControlStickyFooterAd,
  registerSlot,
  registerRewardedSlot,
  triggerPageInit,
  destroyStickyFooterSlot,
  reInitStickyFooterSlot,
  isReady,
};
export default adManager;
